import { type PropsWithChildren } from 'react';
import { LinearGradient } from 'expo-linear-gradient';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { useResponsive } from '@garnish/core';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderModalFooter = (props: Props) => {
  const { children } = props;

  const { currentBreakpoint, match } = useResponsive();
  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();

  // ─── Styles ──────────────────────────────────────────────────────────

  const safeAreaBottomStyle = useStyle(() => {
    const shouldUseSafeAreaBottomInset = Platform.OS !== 'web' && currentBreakpoint.isXS;

    if (!shouldUseSafeAreaBottomInset) return;

    const bottomSpacing = Math.max(safeAreaBottomInset, theme.spacing['4']);

    return { paddingBottom: bottomSpacing + theme.spacing['2'] };
  }, [currentBreakpoint.isXS, safeAreaBottomInset]);

  const contentContainerStyles = [
    styles.contentContainer,
    match([undefined, styles.contentContainerSM]),
    safeAreaBottomStyle,
  ];

  return (
    <View style={styles.container} pointerEvents="box-none">
      <LinearGradient
        style={styles.gradientContainer}
        pointerEvents="none"
        colors={GRADIENT_COLORS.BACK_LAYER}
      />
      <LinearGradient
        style={styles.gradientContainer}
        pointerEvents="none"
        colors={GRADIENT_COLORS.FRONT_LAYER}
      />

      <View style={contentContainerStyles} pointerEvents="box-none">
        {children}
      </View>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const GRADIENT_COLORS = {
  BACK_LAYER: [
    theme.colors.OPACITY.BLACK.TRANSPARENT,
    theme.colors.OPACITY.BLACK.LIGHTEST,
  ] as const,
  FRONT_LAYER: [theme.colors.OPACITY.OATMEAL.TRANSPARENT, theme.colors.APP_BACKGROUND] as const,
};

const GRADIENT_CONTAINER_HEIGHT = 40;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  gradientContainer: {
    position: 'absolute',
    top: -GRADIENT_CONTAINER_HEIGHT,
    right: 0,
    left: 0,
    height: GRADIENT_CONTAINER_HEIGHT,
  },
  contentContainer: {
    paddingHorizontal: theme.spacing['4'],
    paddingTop: theme.spacing['4'],
    paddingBottom: theme.spacing['8'],
  },
  contentContainerSM: {
    paddingHorizontal: theme.spacing['8'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren;
